// src/components/weeklyReport/ReportPatientsList.js
//
// Shared pieces for showing a submitted patient-level weekly report, used by
// ReportsGrid (Admin Reports → Submitted reports, and the report page's past
// reports):
//   reportSummary / reportByline  counts and "submitted … by …"
//   ReportChips    the report's counts as chips
//   ReportDetails  every study with its people (and follow-ups not listed)
// What these are given is already limited by Firestore rules (admins: all; a
// coordinator: their own).

import { StatusChip } from "./ui";
import { REPORT_STATUSES, reportTotals, countValue } from "../../services/weeklyReportModel";
import { approvalLabel } from "../../services/studyReportApproval";

export const fmtSubmitted = (ms) =>
  ms
    ? new Date(ms).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
    : "";

/** Totals for a saved report: each study's people plus its follow-ups not listed by name. */
export const reportSummary = (report) => {
  const studies = report?.studies || [];
  return {
    studies,
    patientCount: studies.reduce((n, s) => n + (s.patients || []).length, 0),
    totals: reportTotals(studies.map((s) => ({ patients: s.patients || [], followUpCount: s.followUpsNotListed }))),
    // Pre-screens not tied to a study (interim staging reports used `preScreens`).
    preScreensNotTied: countValue(report?.preScreensNotTied ?? report?.preScreens),
  };
};

/** "3 studies · 5 patients · submitted Oct 8, 2:14 PM by someone@…" */
export const reportByline = (report) => {
  const { studies, patientCount } = reportSummary(report);
  return [
    `${studies.length} ${studies.length === 1 ? "study" : "studies"} · ${patientCount} ${patientCount === 1 ? "patient" : "patients"}`,
    report?.createdAtMs ? ` · submitted ${fmtSubmitted(report.createdAtMs)}` : "",
    report?.authorEmail ? ` by ${report.authorEmail}` : "",
  ].join("");
};

export const ReportChips = ({ report }) => {
  const { totals, preScreensNotTied } = reportSummary(report);
  return (
    <>
      {REPORT_STATUSES.filter((s) => totals[s.field] > 0).map((s) => (
        <StatusChip key={s.id} status={s.id}>
          {s.label} {totals[s.field]}
        </StatusChip>
      ))}
      {preScreensNotTied > 0 && <StatusChip status="preScreen">Pre-screens (no study) {preScreensNotTied}</StatusChip>}
    </>
  );
};

// approval: the report's counts doc ({ studies, studyApprovals }), when loaded.
// Laid out to read top to bottom in a pop-up: the report's totals first, then
// one block per study (its own totals, then each person on their own row;
// long names wrap instead of being cut off).
export const ReportDetails = ({ report, approval }) => {
  const { studies, preScreensNotTied } = reportSummary(report);
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-1.5">
        <ReportChips report={report} />
      </div>
      {preScreensNotTied > 0 && (
        <div className="weekly-report-row rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-slate-200">
          Pre-screens not tied to a study: <span className="font-semibold">{preScreensNotTied}</span>
        </div>
      )}
      {studies.map((s) => {
        const totals = reportTotals([{ patients: s.patients || [], followUpCount: s.followUpsNotListed }]);
        const label = approval && approvalLabel(approval, s.studyDocId);
        return (
          <section
            key={s.studyDocId}
            className="weekly-report-row rounded-xl border border-white/10 bg-white/[0.03] p-3 space-y-2"
          >
            <div>
              <h3 className="text-sm font-semibold text-white break-words">
                {s.studyId ? `${s.studyId} – ` : ""}
                {s.name}
              </h3>
              {(countValue(s.followUpsNotListed) > 0 || label) && (
                <p className="text-[11px] text-slate-400">
                  {countValue(s.followUpsNotListed) > 0 &&
                    `${countValue(s.followUpsNotListed)} follow-ups not listed by name`}
                  {countValue(s.followUpsNotListed) > 0 && label ? " · " : ""}
                  {label}
                </p>
              )}
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {REPORT_STATUSES.filter((st) => totals[st.field] > 0).map((st) => (
                  <StatusChip key={st.id} status={st.id}>
                    {st.label} {totals[st.field]}
                  </StatusChip>
                ))}
              </div>
            </div>
            {(s.patients || []).length > 0 ? (
              <ul className="space-y-1.5">
                {(s.patients || []).map((p) => (
                  <li
                    key={p.key}
                    className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 px-3 py-2 rounded-lg border border-white/5 bg-white/[0.02]"
                  >
                    <span className="min-w-0 flex-1 text-sm text-white break-words">
                      {p.name || "(no name)"}
                      {p.notInRegx && <span className="ml-2 text-[10px] font-medium text-amber-300">not in RegX</span>}
                    </span>
                    <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-400">
                      <span>MRN {p.mrn || "—"}</span>
                      <span>DOB {p.dob || "—"}</span>
                      {p.subjectNumber && <span>Subject {p.subjectNumber}</span>}
                      <StatusChip status={p.status} />
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-[11px] text-slate-400">No patients listed by name for this study.</p>
            )}
          </section>
        );
      })}
    </div>
  );
};
